import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, MapPin, Ticket } from "lucide-react";

// -----------------------------------------------------------------------------
// EVENT: I STAY WITH ME
// A special engagement presented by Birmingham Festival Theatre at Encore.
// Two nights only: Sept 19–20, 2026, 7:00 PM
// -----------------------------------------------------------------------------

const TICKET_URL = "https://www.onthestage.tickets/show/birmingham-festival-theatre/6a9c91e6f708187865320a8c/tickets#/productions-view";
const POSTER_IMG = "/images/istay-poster.jpg";
const HEADSHOT_IMG = "/images/markeitra-headshot.jpg";

export default function IStayWithMe() {
  return (
    <div data-testid="event-i-stay-with-me" className="pt-32 pb-24 bg-stone-950">
      {/* Return link */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10">
        <Link
          to="/"
          data-testid="event-back"
          className="inline-flex items-center gap-2 font-overline text-[0.7rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
        >
          <ArrowLeft size={14} /> Back to Homepage
        </Link>
      </div>

      {/* HERO — poster + headline block */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Poster (portrait) */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative aspect-[2/3] w-full overflow-hidden border border-[#DE9E48]/25 shadow-[0_30px_80px_-30px_rgba(222,158,72,0.35)]">
            <img
              src={POSTER_IMG}
              alt="I Stay With Me — a one-woman show poster"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              data-testid="event-poster"
            />
          </div>
          <p className="font-overline text-[0.6rem] tracking-[0.22em] text-stone-500 mt-4 text-center">
            — A Bag'z Production · Poster Art
          </p>
        </div>

        {/* Headline copy */}
        <div className="lg:col-span-7 order-1 lg:order-2 space-y-8">
          <div>
            <p className="font-overline text-[0.65rem] tracking-[0.28em] text-[#DE9E48] mb-4">
              — Birmingham Festival Theatre Presents
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-[#FDFBF7]">
              I Stay <span className="italic text-[#DE9E48]">With Me.</span>
            </h1>
            <p className="font-body italic text-stone-300 mt-6 text-lg md:text-xl max-w-xl leading-relaxed">
              An intimate new one-woman show by Markeitra "Queen Key" Gilliam.
              Two nights only at Encore Theatre and Gallery.
            </p>
          </div>

          {/* Details grid */}
          <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
            <DetailBlock icon={Calendar} label="Dates">
              September 19 &amp; 20, 2026
              <br />
              <span className="text-stone-500">Two Nights · Two Shows</span>
            </DetailBlock>
            <DetailBlock icon={Clock} label="Showtime">
              7:00 PM
              <br />
              <span className="text-stone-500">Doors 6:30 PM</span>
            </DetailBlock>
            <DetailBlock icon={MapPin} label="Venue">
              Encore Theatre &amp; Gallery
              <br />
              <span className="text-stone-500">
                1604 2nd Avenue North, Birmingham, AL 35203
              </span>
            </DetailBlock>
            <DetailBlock icon={Ticket} label="Tickets">
              $15 Advance
              <br />
              <span className="text-stone-500">$20 At the Door</span>
            </DetailBlock>
          </div>

          {/* Primary CTA */}
          <div className="flex flex-wrap items-center gap-5 pt-2">
            <a
              data-testid="event-cta"
              href={TICKET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#7A431D] hover:bg-[#5C331A] text-white font-overline text-sm tracking-[0.18em] uppercase rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(222,158,72,0.5)] hover:-translate-y-0.5"
            >
              <Ticket size={16} strokeWidth={2.5} />
              Reserve Tickets
              <ArrowUpRight size={14} />
            </a>
            <p className="font-overline text-[0.65rem] tracking-[0.14em] text-stone-500 max-w-xs leading-relaxed">
              Tickets &amp; info via Birmingham Festival Theatre
              <br />
              <span className="text-stone-600">onthestage.tickets · #IStayWithMe</span>
            </p>
          </div>
        </div>
      </section>

      {/* PROSE — press release body */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 mt-24 md:mt-32 space-y-8 font-body text-stone-300 text-[1.05rem] leading-[1.8]">
        <p className="font-display italic text-2xl md:text-3xl leading-snug text-[#FDFBF7]">
          What happens when the life you planned no longer looks like the life
          you're living—and you have no idea what comes next?
        </p>

        <p>
          <span className="text-[#DE9E48] font-medium">
            Birmingham Festival Theatre presents I Stay With Me,
          </span>{" "}
          an original one-woman show written and performed by multidisciplinary
          theatre artist Markeitra "Queen Key" Gilliam, for two performances only,
          September 19 and 20 at 7:00 p.m. at Encore Theatre and Gallery,
          1604 2nd Avenue North in downtown Birmingham.
        </p>

        <p className="font-display text-xl md:text-2xl text-stone-100 py-2">
          Part theatre. Part poetry. Part music. Part confession.
        </p>

        <p>
          I Stay With Me is an intimate, independent performance experience
          about getting lost, beginning again, and learning that when everything
          else feels uncertain, there is still one person you cannot afford to
          abandon: yourself.
        </p>

        <p>
          Blending storytelling, poetry, original music, humor and personal
          reflection, Gilliam invites audiences into the beautifully unfinished
          territory between who we thought we would become and who we actually
          are.
        </p>

        <p>
          There is no tidy roadmap here. No miraculous revelation that suddenly
          makes everything make sense. Instead, I Stay With Me sits inside the
          messier truth: sometimes we wander. Sometimes plans disappear.
          Sometimes the familiar stops feeling like home. And sometimes moving
          forward begins simply by deciding to remain present for yourself.
        </p>

        <p>
          The result is a deeply personal piece of independent theatre that feels
          less like watching someone perform from a distance and more like being
          invited into the room while an artist wrestles, remembers, laughs,
          questions and creates her way forward.
        </p>
      </section>

      {/* ABOUT THE ARTIST — headshot + bio, split layout */}
      <section className="mt-24 md:mt-32 border-t border-white/10 pt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Headshot */}
          <div className="md:col-span-5">
            <div className="relative aspect-[3/4] w-full overflow-hidden">
              <img
                src={HEADSHOT_IMG}
                alt="Markeitra 'Queen Key' Gilliam"
                className="absolute inset-0 w-full h-full object-cover grayscale-[0.15]"
                loading="lazy"
                data-testid="event-headshot"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[#DE9E48]/25" />
            </div>
            <p className="font-overline text-[0.6rem] tracking-[0.22em] text-stone-500 mt-4">
              — Markeitra "Queen Key" Gilliam
            </p>
          </div>

          {/* Bio */}
          <div className="md:col-span-7 space-y-6 font-body text-stone-300 leading-[1.75] text-[1rem]">
            <p className="font-overline text-[0.65rem] tracking-[0.28em] text-[#DE9E48]">
              — About the Artist
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter text-[#FDFBF7]">
              An artist wrestling{" "}
              <span className="italic text-[#DE9E48]">her way forward.</span>
            </h2>

            <p>
              Markeitra "Queen Key" Gilliam is an actor, director, writer, singer,
              songwriter, poet and theatre artist originally from New Orleans,
              Louisiana. She has trained at the University of Alabama at Birmingham
              (UAB) and Dell'Arte International School of Physical Theatre,
              developing a multidisciplinary approach to performance and
              storytelling.
            </p>

            <p>
              Her theatrical work has taken her onstage and behind the scenes in
              New York City, California, Louisiana and Alabama, with experience
              spanning acting, directing, assistant directing, production and
              stage management. Those artistic disciplines converge in{" "}
              <em className="text-[#FDFBF7] not-italic">I Stay With Me</em>, with
              Gilliam serving simultaneously as playwright and performer.
            </p>

            <p>
              The Birmingham engagement arrives at a transitional moment in her
              own artistic story. Following the performances, she will travel to
              London to continue her professional acting training at the{" "}
              <span className="text-[#DE9E48] font-medium">
                Giles Foreman Centre for Acting
              </span>
              , making I Stay With Me both a reflection on the road behind her
              and a threshold before whatever comes next.
            </p>

            <blockquote className="border-l-2 border-[#DE9E48] pl-6 py-2 font-display italic text-xl md:text-2xl text-stone-100 leading-snug mt-8">
              Because sometimes you don't have the answers. Sometimes you don't
              know the way. But wherever life takes you, you stay with you.
            </blockquote>
          </div>
        </div>
      </section>

      {/* CLOSING CTA STRIP */}
      <section className="mt-24 md:mt-32 border-y border-[#DE9E48]/25 bg-gradient-to-br from-[#2a2124] via-[#3a2a22] to-[#2a2124]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 space-y-3">
            <p className="font-overline text-[0.65rem] tracking-[0.28em] text-[#DE9E48]">
              — Two Nights Only
            </p>
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1] text-[#FDFBF7]">
              Sep 19 &amp; 20 · 7:00 PM
            </h3>
            <p className="font-body italic text-stone-400 text-base md:text-lg max-w-xl">
              Encore Theatre &amp; Gallery · 1604 2nd Avenue North, Birmingham
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              data-testid="event-cta-bottom"
              href={TICKET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#7A431D] hover:bg-[#5C331A] text-white font-overline text-sm tracking-[0.18em] uppercase rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(222,158,72,0.5)] hover:-translate-y-0.5"
            >
              <Ticket size={16} strokeWidth={2.5} />
              Reserve Tickets
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* MEDIA CONTACT */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 mt-16 text-center">
        <p className="font-overline text-[0.6rem] tracking-[0.28em] text-stone-500 mb-2">
          — Media Contact
        </p>
        <p className="font-body text-stone-400 text-sm">
          Rhonda Erbrick ·{" "}
          <a
            href="mailto:rherbrick@gmail.com"
            className="text-[#DE9E48] hover:text-[#F0B770] underline decoration-dotted underline-offset-4"
          >
            rherbrick@gmail.com
          </a>
        </p>
      </section>
    </div>
  );
}

function DetailBlock({ icon: Icon, label, children }) {
  return (
    <div className="border border-white/10 rounded-xl p-5 bg-stone-900/40">
      <div className="flex items-center gap-2 mb-2 text-[#DE9E48]">
        <Icon size={14} strokeWidth={2} />
        <p className="font-overline text-[0.6rem] tracking-[0.22em]">{label}</p>
      </div>
      <p className="font-body text-stone-200 text-[0.95rem] leading-relaxed">
        {children}
      </p>
    </div>
  );
}
